import React, { useState, useCallback } from 'react';
import { setHours } from 'date-fns';
import { FaFileDownload, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import SimpleCameraModal from '~/components/SimpleCameraModal';
import {
  Container,
  Content,
  Header,
  Form,
  ProtocolInfo,
  InputGroup,
  Button,
  CancelButton,
} from './styles';

const MobileReceiverModal = ({
  isOpen,
  setIsOpen,
  handleWriteOff,
  protocolCod,
  client,
  method,
  forcedDate,
}) => {
  const [receiverName, setReceiverName] = useState('');
  // Se vier forcedDate, usa ela e trava o input
  const [receiverDate, setReceiverDate] = useState(() => {
    if (forcedDate) return forcedDate;
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Novo estado para imagem capturada
  const [photoBase64, setPhotoBase64] = useState(null);
  const [showCamera, setShowCamera] = useState(false);

  // Função chamada pelo componente de câmera
  const handlePhotoTaken = useCallback(base64 => {
    setPhotoBase64(base64);
    setShowCamera(false);
    toast.success('Foto capturada com sucesso!');
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();

    if (!receiverName.trim()) {
      toast.warning('Por favor, informe o nome do receptor');
      return;
    }

    if (!receiverDate) {
      toast.warning('Por favor, informe a data do recebimento');
      return;
    }

    if (!protocolCod) {
      toast.error('Erro: dados do protocolo não encontrados');
      return;
    }

    if (!photoBase64) {
      toast.warning(
        'Por favor, tire uma foto do documento antes de dar baixa.'
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const formattedDate = setHours(new Date(receiverDate), 8);
      await handleWriteOff(receiverName.trim(), formattedDate, photoBase64);

      // Reset form after successful submission
      setReceiverName('');
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      setReceiverDate(`${year}-${month}-${day}`);
      setPhotoBase64(null);
    } catch (error) {
      toast.error('Erro ao dar baixa no protocolo. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return; // Prevent closing while submitting

    setIsOpen(false);
    setReceiverName('');
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    setReceiverDate(`${year}-${month}-${day}`);
    setPhotoBase64(null);
  };

  if (!isOpen) return null;

  return (
    <Container>
      <Content>
        <Header>
          <div className="header-content">
            <FaFileDownload size={24} color="#01579b" />
            <h3>Dar Baixa no Protocolo</h3>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="close-button"
            disabled={isSubmitting}
          >
            <FaTimes size={18} color="#e53935" />
          </button>
        </Header>

        <Form onSubmit={handleSubmit}>
          <ProtocolInfo>
            <p>
              Baixar {method === 1 ? 'Protocolo ' : 'Arquivo Morto '}
              <strong>#{protocolCod || 'N/A'}</strong>
            </p>
            <p>
              Cliente: <strong>{client || 'Cliente não informado'}</strong>
            </p>
          </ProtocolInfo>

          <InputGroup>
            <label htmlFor="receiverName">Nome do Receptor</label>
            <input
              id="receiverName"
              type="text"
              value={receiverName}
              onChange={e => setReceiverName(e.target.value)}
              placeholder="Digite o nome de quem recebeu"
              disabled={isSubmitting}
            />
          </InputGroup>

          <InputGroup>
            <label htmlFor="receiverDate">Data do Recebimento</label>
            <input
              id="receiverDate"
              type="date"
              value={receiverDate}
              disabled
            />
          </InputGroup>

          <InputGroup>
            <label>Foto do Documento</label>
            {photoBase64 ? (
              <div style={{ marginBottom: 8 }}>
                <img
                  src={photoBase64}
                  alt="Foto do documento"
                  style={{
                    maxWidth: '100%',
                    maxHeight: 180,
                    borderRadius: 8,
                    border: '1px solid #ccc',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setPhotoBase64(null)}
                  disabled={isSubmitting}
                  style={{ marginTop: 4 }}
                >
                  Remover Foto
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowCamera(true)}
                disabled={isSubmitting}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 16px',
                  background: '#01579b',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    style={{ marginRight: 6 }}
                  >
                    <path d="M12 17a4.978 4.978 0 0 1-4.9-4H5a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h2.382l.724-1.447A2 2 0 0 1 9.882 5h4.236a2 2 0 0 1 1.776 1.553L16.618 7H19a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-2.1a4.978 4.978 0 0 1-4.9 4zm0-2a2.978 2.978 0 0 0 2.816-2H9.184A2.978 2.978 0 0 0 12 15zm-7-7v4h2.382l.724-1.447A2 2 0 0 1 9.882 9h4.236a2 2 0 0 1 1.776 1.553L16.618 11H19V7h-2.382l-.724 1.447A2 2 0 0 1 14.118 9H9.882a2 2 0 0 1-1.776-1.553L7.382 7H5zm7 8a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" />
                  </svg>
                  Tirar Foto
                </span>
              </button>
            )}
          </InputGroup>

          <div className="button-group">
            <CancelButton
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancelar
            </CancelButton>
            <Button type="submit" disabled={isSubmitting}>
              <FaFileDownload />
              {isSubmitting ? 'Processando...' : 'Dar Baixa'}
            </Button>
          </div>
        </Form>

        {/* Modal da câmera para tirar foto */}
        {showCamera && (
          <SimpleCameraModal
            isOpen={showCamera}
            setIsOpen={setShowCamera}
            onBarcodeDetected={null}
            onPhotoTaken={handlePhotoTaken}
          />
        )}
      </Content>
    </Container>
  );
};

export default MobileReceiverModal;
